// src/components/appSettings/FeatureFlags/FeatureFlagsManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiToggleLeft,
  FiToggleRight,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDollarSign,
  FiPercent,
  FiShield,
  FiPackage,
  FiTruck,
  FiCreditCard,
  FiGlobe,
  FiSettings,
  FiAlertCircle,
  FiCheck,
  FiX,
  FiUsers,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiClock,
} from "react-icons/fi";
import {
  fetchAllConfigsThunk,
  createAppConfigThunk,
  updateAppConfigThunk,
  deleteConfigThunk,
  updateFeatureFlagsThunk,
  bulkUpdateFeaturesThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const FeatureFlagsManager = () => {
  const dispatch = useDispatch();

  // Redux state
  const { configs, loadingList } = useSelector((state) => state.appSettings);

  // Filter only feature flag configs
  const featureFlags =
    configs?.filter(
      (config) =>
        config.category === "FEATURE" ||
        config.category === "FEATURES" ||
        config.key?.startsWith("feature_") ||
        config.isFeatureFlag
    ) || [];

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFlagHistory, setSelectedFlagHistory] = useState(null);

  // Load configs on mount
  useEffect(() => {
    loadFeatureFlags();
  }, [dispatch]);

  const loadFeatureFlags = useCallback(() => {
    dispatch(fetchAllConfigsThunk());
  }, [dispatch]);

  // Get unique sub-categories from feature flags
  const categories = [
    "all",
    ...new Set(
      featureFlags
        ?.map((flag) => flag.metadata?.group || flag.description?.split(":")[0])
        .filter(Boolean) || []
    ),
  ];

  // Filter flags
  const filteredFlags = featureFlags?.filter((flag) => {
    const matchesSearch =
      flag.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      flag.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      flag.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" ||
      flag.metadata?.group === selectedCategory ||
      flag.description?.startsWith(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  // Group flags by sub-category
  const groupedFlags = filteredFlags?.reduce((acc, flag) => {
    const category = flag.metadata?.group || "General Features";
    if (!acc[category]) acc[category] = [];
    acc[category].push(flag);
    return acc;
  }, {});

  // Handle toggle - use feature flags endpoint
  const handleToggle = async (flagId, currentValue) => {
    try {
      await dispatch(
        updateFeatureFlagsThunk({
          configId: flagId,
          featureData: { isEnabled: !currentValue },
        })
      ).unwrap();

      toast.success("Feature flag updated successfully");
      loadFeatureFlags(); // Reload to get updated data
    } catch (error) {
      toast.error(error.message || "Failed to update feature flag");
    }
  };

  const handleEdit = (flag) => {
    setEditingFlag(flag);
    setShowModal(true);
  };

  const handleDelete = async (flagId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this feature flag? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteConfigThunk(flagId)).unwrap();
        toast.success("Feature flag deleted successfully");
      } catch (error) {
        toast.error(error.message || "Failed to delete feature flag");
      }
    }
  };

  const handleViewHistory = (flag) => {
    setSelectedFlagHistory(flag);
    setShowHistoryModal(true);
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Feature Flags Manager
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Control feature availability across your application
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadFeatureFlags}
            disabled={loadingList}
          >
            <FiRefreshCw className={loadingList ? "animate-spin" : ""} />
          </Button>
          <Button
            onClick={() => {
              setEditingFlag(null);
              setShowModal(true);
            }}
          >
            <FiPlus className="mr-2" />
            Add Feature Flag
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Flags"
          value={featureFlags?.length || 0}
          icon={FiToggleRight}
          color="blue"
        />
        <StatCard
          label="Enabled"
          value={
            featureFlags?.filter((f) => f.value === true || f.value > 0)
              .length || 0
          }
          icon={FiCheck}
          color="green"
        />
        <StatCard
          label="Disabled"
          value={
            featureFlags?.filter((f) => f.value === false || f.value === 0)
              .length || 0
          }
          icon={FiX}
          color="red"
        />
        <StatCard
          label="Categories"
          value={categories.length - 1}
          icon={FiFilter}
          color="purple"
        />
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search feature flags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === "all" ? "All Categories" : category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Feature Flags List */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredFlags?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiToggleLeft className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500">No feature flags found</p>
          <Button
            className="mt-4"
            onClick={() => {
              setEditingFlag(null);
              setShowModal(true);
            }}
          >
            Create Your First Feature Flag
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFlags || {}).map(([category, flags]) => (
            <div key={category} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b bg-gray-50">
                <h2 className="text-lg font-medium text-gray-900">
                  {category}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {flags.length} flag{flags.length !== 1 ? "s" : ""}
                </p>
              </div>

              <div className="divide-y">
                {flags.map((flag) => (
                  <FeatureFlagRow
                    key={flag._id}
                    flag={flag}
                    onToggle={handleToggle}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onViewHistory={handleViewHistory}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <FeatureFlagModal
          flag={editingFlag}
          onClose={() => {
            setShowModal(false);
            setEditingFlag(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingFlag(null);
            loadFeatureFlags();
          }}
        />
      )}

      {showHistoryModal && selectedFlagHistory && (
        <HistoryModal
          flag={selectedFlagHistory}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedFlagHistory(null);
          }}
        />
      )}
    </div>
  );
};

// Feature Flag Row Component
const FeatureFlagRow = ({
  flag,
  onToggle,
  onEdit,
  onDelete,
  onViewHistory,
}) => {
  const getValueDisplay = () => {
    if (flag.dataType === "BOOLEAN") {
      return flag.value ? "Enabled" : "Disabled";
    }
    if (flag.dataType === "NUMBER") {
      return `${flag.value}${flag.unit || ""}`;
    }
    return String(flag.value || "-");
  };

  const isEnabled =
    flag.dataType === "BOOLEAN" ? flag.value === true : flag.value > 0;

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <FiToggleRight className="text-gray-600" />
            <h3 className="font-medium text-gray-900">
              {flag.name || flag.key}
            </h3>
            <span
              className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                isEnabled
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {getValueDisplay()}
            </span>
            {flag.isRequired && (
              <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                Required
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3">{flag.description}</p>

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span>Key: {flag.key}</span>
            {flag.dataType && <span>Type: {flag.dataType}</span>}
            {flag.updatedAt && (
              <span>Updated: {new Date(flag.updatedAt).toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => onToggle(flag._id, flag.value)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isEnabled ? "bg-primary" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(flag)}
            title="Edit"
          >
            <FiEdit2 size={14} />
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(flag._id)}
            className="text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <FiTrash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Feature Flag Modal Component
const FeatureFlagModal = ({ flag, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    key: flag?.key || "",
    name: flag?.name || "",
    description: flag?.description || "",
    category: "FEATURE",
    dataType: flag?.dataType || "BOOLEAN",
    value: flag?.value !== undefined ? flag.value : false,
    metadata: {
      group: flag?.metadata?.group || "General Features",
      helpText: flag?.metadata?.helpText || "",
    },
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let parsedValue = formData.value;

      // Parse value based on data type
      if (formData.dataType === "NUMBER") {
        parsedValue = parseFloat(formData.value);
      } else if (formData.dataType === "BOOLEAN") {
        parsedValue = formData.value === "true" || formData.value === true;
      }

      const submitData = {
        ...formData,
        value: parsedValue,
        isFeatureFlag: true, // Mark as feature flag
      };

      if (flag) {
        // Update existing config
        await dispatch(
          updateAppConfigThunk({
            id: flag._id,
            configData: submitData,
          })
        ).unwrap();
        toast.success("Feature flag updated successfully");
      } else {
        // Create new config
        await dispatch(createAppConfigThunk(submitData)).unwrap();
        toast.success("Feature flag created successfully");
      }

      onSave();
    } catch (error) {
      toast.error(error.message || "Failed to save feature flag");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {flag ? "Edit Feature Flag" : "Add Feature Flag"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Feature name"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Key *
              </label>
              <input
                type="text"
                name="key"
                value={formData.key}
                onChange={handleChange}
                required
                disabled={!!flag}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100"
                placeholder="feature_key_name"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Brief description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group/Category
              </label>
              <input
                type="text"
                name="metadata.group"
                value={formData.metadata.group}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., Payment Features"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Type
              </label>
              <select
                name="dataType"
                value={formData.dataType}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="BOOLEAN">Boolean (On/Off)</option>
                <option value="NUMBER">Number/Percentage</option>
              </select>
            </div>

            {formData.dataType === "BOOLEAN" && (
              <div className="col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="value"
                    checked={formData.value}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Enabled
                  </span>
                </label>
              </div>
            )}

            {formData.dataType === "NUMBER" && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value (0-100)
                </label>
                <input
                  type="number"
                  name="value"
                  value={formData.value}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitting ? "Saving..." : flag ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// History Modal Component (placeholder)
const HistoryModal = ({ flag, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            Change History: {flag.name}
          </h2>
        </div>

        <div className="p-6">
          <p className="text-center text-gray-500 py-8">
            History tracking not yet implemented
          </p>
        </div>

        <div className="p-6 border-t flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`text-2xl text-${color}-600`} />
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

export default FeatureFlagsManager;
